import {
    BonusType,
    PaymentBonusType,
} from '@superbet-group/web.types';

import BonusEligibility from '@core/models/bonus/BonusEligibility';
import { MoneyUsage } from '@core/models/bonus/MoneyUsage';
import { WinCreditType } from '@core/models/bonus/WinCreditType';

type BetslipBonusData = {
    activeBonus: Nullable<BonusEligibility>;
    stake: number;
    userBalance: number;
    isSimple?: boolean;
    isBonusSelected: boolean;
};

export function getRealMoney(data: BetslipBonusData): number {
    const { activeBonus, stake, userBalance, isSimple, isBonusSelected } = data;
    if (activeBonus?.balance && isBonusSelected) {
        if (isNetWin(activeBonus) || isBonusMoneyUsedFirst(activeBonus)) {
            return isSimple ? calculateFreeBetRealMoney(activeBonus, stake) : stake;
        } else {
            return calculateSportsRealMoney(activeBonus, stake, userBalance);
        }
    } else {
        return stake;
    }
}

export function getBonusMoney(data: BetslipBonusData): number {
    const { activeBonus, stake, userBalance, isSimple, isBonusSelected } = data;
    if (activeBonus && isBonusSelected) {
        if (isNetWin(activeBonus) || isBonusMoneyUsedFirst(activeBonus)) {
            return isSimple ? calculateFreeBetBonusMoney(activeBonus, stake) : 0;
        } else {
            return calculateSportsBonusMoney(activeBonus, stake, userBalance);
        }
    } else {
        return 0;
    }
}

/**
 * Real money calculation when FreeBet bonus is used.
 * FreeBet bonus is bonus money before real money.
 * If FreeBet bonus money available is less than current stake, rest of the stake should be used from real money
 *
 * @param activeBonus FreeBet bonus
 * @param stake current user stake input
 * @returns amount of real money used with FreeBet bonus
 */
function calculateFreeBetRealMoney(activeBonus: BonusEligibility, stake: number): number {
    if (
        activeBonus.balance
        && activeBonus.balance < stake
    ) {
        return stake - activeBonus.balance;
    } else {
        return 0;
    }
}

/**
 * Bonus money calculation when FreeBet bonus is used.
 * FreeBet bonus is bonus money before real money.
 * If FreeBet bonus money is equal or greater than stake, whole stake is bonus money, otherwise use whole bonus amount
 *
 * @param activeBonus FreeBet bonus
 * @param stake current user stake input
 * @returns amount of bonus money used with FreeBet bonus
 */
function calculateFreeBetBonusMoney(activeBonus: BonusEligibility, stake: number): number {
    if (activeBonus.balance && stake > 0) {
        return activeBonus.balance >= stake ? stake : activeBonus.balance;
    } else {
        return 0;
    }
}

/**
 * Real money calculation when Sports bonus is used.
 * Sport bonus is real money before bonus money.
 * Whole stake is used when user balance is greater or the same as stake,
 * (Stake - Bonus) is used when (stake - user balance) is greater than bonus, else use user balance
 *
 * @param activeBonus Sport bonus
 * @param stake current user stake input
 * @param userBalance current user balance
 * @returns amount of real money used with Sport bonus
 */
function calculateSportsRealMoney(activeBonus: BonusEligibility, stake: number, userBalance: number): number {
    if (userBalance >= stake) {
        return stake > 0 ? stake : 0;
    } else {
        const tmpBonusMoney = stake - userBalance;
        if (activeBonus.balance
            && tmpBonusMoney > activeBonus.balance
        ) {
            return stake - activeBonus.balance;
        } else {
            return userBalance;
        }
    }
}

/**
 * Bonus money calculation when Sports bonus is used.
 * Sport bonus is real money before bonus money.
 * Bonus money is used when (stake - user balance) is greater than bonus, else use (stake - user balance)
 *
 * @param activeBonus Sport bonus
 * @param stake current user stake input
 * @param userBalance current user balance
 * @returns amount of real money used with Sport bonus
 */
function calculateSportsBonusMoney(activeBonus: BonusEligibility, stake: number, userBalance: number): number {
    if (userBalance >= stake) {
        return 0;
    } else {
        const tmpBonusMoney = stake - userBalance;
        if (
            activeBonus.balance
            && tmpBonusMoney > activeBonus.balance
        ) {
            return activeBonus.balance;
        } else {
            return tmpBonusMoney;
        }
    }
}

export function bonusTypeName(activeBonus: BonusEligibility): string {
    if (activeBonus.bonusType) {
        return isSportType(activeBonus) ? 'sport bonus' : 'freeBet';
    } else {
        return isNetWin(activeBonus) ? 'freeBet' : 'sport bonus';
    }
}

export function isNetWin(activeBonus: BonusEligibility): boolean {
    return activeBonus.winType === WinCreditType.Net;
}

export function isSportType(activeBonus: BonusEligibility): boolean {
    return activeBonus.bonusType === BonusType.Sport;
}

function isBonusMoneyUsedFirst(activeBonus: BonusEligibility): boolean {
    return activeBonus.moneyUsage === MoneyUsage.BonusFirst;
}

export function isFreeBetBonusInfoVisible(activeBonus: BonusEligibility, isSimple: boolean): boolean {
    return activeBonus
        && isNetWin(activeBonus)
        && isSimple
        && activeBonus.balance !== undefined
        && activeBonus.balance > 0;
}

export function getPaymentBonusType(data: BetslipBonusData): PaymentBonusType {
    const { activeBonus, stake, userBalance, isBonusSelected } = data;
    if (activeBonus && isBonusSelected) {
        if (isNetWin(activeBonus)) {
            return PaymentBonusType.FreeBet;
        } else if (stake > userBalance) {
            return PaymentBonusType.Wagering;
        } else {
            return PaymentBonusType.NoBonus;
        }
    } else {
        return PaymentBonusType.NoBonus;
    }
}
